import { useState } from 'react';
import PhotoLightbox from '../../shared/PhotoLightbox.jsx';
import { useAssetBase } from '../../../hooks/useEnvironment';

// The customer's own submission photos that the KG identify leg grouped to THIS
// quote item (ItemIdentityCard.photos, Prism spec kg_quote_path). Not the KG's
// catalogue image — these are the pictures the customer uploaded, narrowed to
// the ones that show this item, so the operator sees the actual thing beside
// the KG's answer. Sits below the identity card in the item box.
//
// The full-width Photos strip above still shows every photo on the deal; this
// row is the per-item subset.

// A PhotoRef -> displayable URL: the signed URL if present, else the public URL
// built from the key on this environment's bucket (`assetBase`, from
// /api/environment, the same base the full-width Photos strip uses). Null when
// neither works, including a key-only photo while assetBase is still unknown.
function photoUrl(photo, assetBase) {
    if (photo?.signed_url) return photo.signed_url;
    if (photo?.r2_key && assetBase) return `${assetBase}/${photo.r2_key}`;
    return null;
}

// The item's photos as an ordered list of URLs: Prism's display_order (lead shot
// first), unresolvable refs dropped. A stable copy — never sorts the prop array.
export function orderedPhotoUrls(photos, assetBase) {
    return (photos || [])
        .slice()
        .sort((a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0))
        .map((p) => photoUrl(p, assetBase))
        .filter(Boolean);
}

export default function ItemPhotos({ photos }) {
    // Index shown full screen, or null when the lightbox is closed.
    const [enlarged, setEnlarged] = useState(null);
    const urls = orderedPhotoUrls(photos, useAssetBase());
    if (urls.length === 0) return null;

    // stopPropagation: the item card selects itself on click, and opening a photo
    // must not also re-select and scroll the card out from under the operator.
    return (
        <div className="qa-idc-photos" onClick={e => e.stopPropagation()}>
            <div className="qa-idc-photos-lab">Customer photos for this item</div>
            <div className="qa-strip">
                {urls.map((src, i) => (
                    <img
                        key={src}
                        className="qa-strip-thumb"
                        src={src}
                        alt={`Customer photo ${i + 1} of ${urls.length}`}
                        loading="lazy"
                        onClick={() => setEnlarged(i)}
                    />
                ))}
            </div>
            {enlarged !== null && (
                <PhotoLightbox images={urls} initialIndex={enlarged} onClose={() => setEnlarged(null)} />
            )}
        </div>
    );
}
